import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AUTH_STORAGE_KEYS,
  clearStoredSession,
  getStoredToken,
  getTokenExpiresAt,
  isTokenExpired,
  storeSession
} from '../api/http';

// ==============================|| USER INTERFACE ||============================== //

export interface User {
  id: string;
  name: string;
  email: string;
  idcompanyGroup: string;
  companyName: string;
  [key: string]: any;
}

// ==============================|| USE AUTH HOOK ||============================== //

interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  tokenExpiresAt: number | null;
  login: (token: string, userData?: User | null, tokenExpiresMs?: number) => void;
  logout: () => void;
  checkAuthStatus: () => void;
}

const AuthContext = createContext<UseAuthReturn | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);

  const checkAuthStatus = useCallback((): void => {
    try {
      const token = getStoredToken();
      if (token) {
        if (isTokenExpired()) {
          clearStoredSession();
          setIsAuthenticated(false);
          setUser(null);
          setTokenExpiresAt(null);
          return;
        }

        setIsAuthenticated(true);
        const nextExpiresAt = getTokenExpiresAt();
        setTokenExpiresAt((prev) => (prev === nextExpiresAt ? prev : nextExpiresAt));
        const userData = localStorage.getItem(AUTH_STORAGE_KEYS.user);
        if (userData) {
          try {
            const parsedUser = JSON.parse(userData) as User;
            setUser((prev) => (prev && prev.id === parsedUser.id ? prev : parsedUser));
          } catch (e) {
            localStorage.removeItem(AUTH_STORAGE_KEYS.user);
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setTokenExpiresAt(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = useCallback(
    (token: string, userData: User | null = null, tokenExpiresMs?: number): void => {
      storeSession(token, tokenExpiresMs);
      setTokenExpiresAt(getTokenExpiresAt());
      if (userData) {
        localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(userData));
        setUser(userData);
      }
      setIsAuthenticated(true);
    },
    []
  );

  const logout = useCallback((): void => {
    clearStoredSession();
    setIsAuthenticated(false);
    setUser(null);
    setTokenExpiresAt(null);
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      tokenExpiresAt,
      login,
      logout,
      checkAuthStatus
    }),
    [checkAuthStatus, isAuthenticated, isLoading, login, logout, tokenExpiresAt, user]
  );

  return createElement(AuthContext.Provider, { value }, children);
};

const useAuth = (): UseAuthReturn => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default useAuth;
